import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/ui/PageHero';

function Help() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    { id: 'all', name: 'All Topics', icon: '📚' },
    { id: 'getting-started', name: 'Getting Started', icon: '🚀' },
    { id: 'discovery', name: 'Discovery & Matching', icon: '🔍' },
    { id: 'messaging', name: 'Messaging & Chat', icon: '💬' },
    { id: 'profile', name: 'Profile & Settings', icon: '👤' },
    { id: 'subscription', name: 'Subscription & Pro', icon: '⭐' },
    { id: 'safety', name: 'Safety & Privacy', icon: '🛡️' },
  ];

  const helpArticles = [
    {
      id: 1,
      category: 'getting-started',
      title: 'How to Create Your Profile',
      description: 'Step-by-step guide to setting up your Strapin profile with photos, bio, and preferences.',
      icon: '👤',
      content: `
        <h3>Creating Your Profile</h3>
        <ol>
          <li>Download and open the Strapin app</li>
          <li>Sign in with Google or Apple — no password needed</li>
          <li>Verify your age (18+ required)</li>
          <li>Add up to 6 photos (we recommend at least 3)</li>
          <li>Write a bio that shows your personality</li>
          <li>Set your interests and what you're looking for</li>
          <li>Enable location services for nearby discovery</li>
        </ol>
        <p><strong>Pro Tip:</strong> A complete profile with clear photos gets significantly more connections. Your first photo is your first impression.</p>
      `,
    },
    {
      id: 2,
      category: 'getting-started',
      title: 'Understanding Your Dashboard',
      description: 'Learn about the main tabs and features in the Strapin app.',
      icon: '📱',
      content: `
        <h3>Main Navigation</h3>
        <ul>
          <li><strong>Discover:</strong> Browse profiles of gay and queer people near you</li>
          <li><strong>Chat:</strong> Your conversations — Chats, Albums, and Favourites</li>
          <li><strong>Views:</strong> See who's viewed your profile, and Pokes you've received</li>
          <li><strong>Profile:</strong> Your profile, edit settings, and account management</li>
        </ul>
        <p>All tabs are protected — screenshots and screen recordings are blocked throughout the app.</p>
      `,
    },
    {
      id: 3,
      category: 'discovery',
      title: 'How Discovery Works',
      description: "Learn how to find compatible connections nearby using Strapin's discovery system.",
      icon: '🔍',
      content: `
        <h3>Location-Based Discovery</h3>
        <p>Strapin shows you gay and queer people nearby, sorted by distance. You see their approximate distance — never their exact location, and they never see yours.</p>
        <ul>
          <li><strong>Grid View:</strong> Browse everyone nearby at a glance</li>
          <li><strong>Profile View:</strong> Tap any card to see their full profile, photos, and bio</li>
          <li><strong>Active Status:</strong> See when someone was last active</li>
          <li><strong>Interests:</strong> Filter by shared interests during onboarding</li>
        </ul>
        <p>Your visibility is in your control — you can appear offline or limit who can see you at any time from Settings.</p>
      `,
    },
    {
      id: 4,
      category: 'discovery',
      title: 'Using Pokes',
      description: 'Show interest without sending a message — a low-pressure way to connect.',
      icon: '👋',
      content: `
        <h3>How Pokes Work</h3>
        <ol>
          <li>Open any profile you're interested in</li>
          <li>Tap the red hand (poke) button</li>
          <li>They'll receive a poke notification</li>
          <li>If they poke back, you're connected — start a conversation!</li>
        </ol>
        <p>Pokes appear under the <strong>Views → Pokes</strong> tab. It's a no-pressure signal — no opener required, no obligation to respond.</p>
      `,
    },
    {
      id: 5,
      category: 'messaging',
      title: 'Sending Messages and Media',
      description: 'Learn how to chat, send photos, videos, and share private albums.',
      icon: '💬',
      content: `
        <h3>Messaging Features</h3>
        <ul>
          <li><strong>Text Messages:</strong> Tap the message box and type. Chats stay private inside the app.</li>
          <li><strong>Send Image:</strong> Share a photo from your gallery — saved to the conversation.</li>
          <li><strong>Send Video:</strong> Share video clips privately.</li>
          <li><strong>One-Time Image / Video:</strong> Media that disappears after a single view. Perfect for privacy-sensitive content.</li>
          <li><strong>Take Photo:</strong> Capture and send directly from camera.</li>
          <li><strong>Send Location:</strong> Share your approximate location.</li>
          <li><strong>Share Album:</strong> Share a private album you've created.</li>
        </ul>
        <p><strong>Important:</strong> Screenshots are blocked inside all chats. Your conversations stay private.</p>
      `,
    },
    {
      id: 6,
      category: 'messaging',
      title: 'Understanding Message Status',
      description: 'What the different message indicators mean inside Strapin.',
      icon: '✓',
      content: `
        <h3>Message Status Indicators</h3>
        <ul>
          <li><strong>Single Check (✓):</strong> Message sent successfully</li>
          <li><strong>Double Check (✓✓):</strong> Message delivered to recipient's device</li>
        </ul>
        <p>Your conversations stay inside Strapin. Screenshots are blocked in all chats.</p>
      `,
    },
    {
      id: 7,
      category: 'profile',
      title: 'Editing Your Profile',
      description: 'How to update your photos, bio, and profile information.',
      icon: '✏️',
      content: `
        <h3>Updating Your Profile</h3>
        <ol>
          <li>Go to the <strong>Profile</strong> tab</li>
          <li>Tap <strong>Edit Profile</strong></li>
          <li>Update photos (up to 6), your name, or bio</li>
          <li>Scroll down for basic info fields</li>
          <li>Tap <strong>Save</strong> when done</li>
        </ol>
        <p>New or updated photos go through a brief verification process before appearing on your profile. Changes to bio and name are instant.</p>
      `,
    },
    {
      id: 8,
      category: 'profile',
      title: 'Creating and Sharing Private Albums',
      description: 'How to create photo or video albums and share them selectively.',
      icon: '📁',
      content: `
        <h3>Private Albums</h3>
        <ul>
          <li><strong>Create Album:</strong> Go to Chat → Albums → tap the + icon</li>
          <li><strong>Add Media:</strong> Add photos from gallery, take a photo, or add video</li>
          <li><strong>Name Your Album:</strong> Give it a title</li>
          <li><strong>Share in Chat:</strong> In any conversation, tap + → Share Album → choose an album</li>
          <li><strong>View Duration:</strong> Recipients can view for the time you choose</li>
          <li><strong>Revoke Access:</strong> Remove access from any shared album at any time from your Albums list</li>
        </ul>
        <p>Albums are private by default. Only people you explicitly share with can view them — and screenshots are blocked.</p>
      `,
    },
    {
      id: 9,
      category: 'subscription',
      title: 'Strapin Pro Features',
      description: 'Everything included with a Strapin Pro subscription.',
      icon: '⭐',
      content: `
        <h3>Pro Features</h3>
        <ul>
          <li>✅ See more nearby profiles (free users see limited results per discovery mode)</li>
          <li>✅ Up to 10 albums — create and share more</li>
          <li>✅ Unlimited one-time media & album shares</li>
          <li>✅ Read receipts on messages</li>
          <li>✅ Typing indicators</li>
          <li>✅ Ad-free experience</li>
          <li>✅ Priority customer support</li>
        </ul>
        <p>Subscribe in-app: <strong>Profile → Settings → Account Details → Subscription</strong></p>
      `,
    },
    {
      id: 10,
      category: 'subscription',
      title: 'Managing Your Subscription',
      description: 'How to subscribe, cancel, or manage your Strapin Pro plan.',
      icon: '💳',
      content: `
        <h3>Subscription Management</h3>
        <ul>
          <li><strong>Subscribe:</strong> Profile → Settings → Account Details → Subscription</li>
          <li><strong>Cancel (iOS):</strong> Settings app → Apple ID → Subscriptions → Strapin</li>
          <li><strong>Cancel (Android):</strong> Google Play → Subscriptions → Strapin</li>
          <li><strong>Refunds:</strong> Contact support@strapinapp.com for refund requests</li>
        </ul>
        <p>Subscriptions auto-renew unless cancelled before the renewal date. You retain Pro features until the end of your current billing period.</p>
      `,
    },
    {
      id: 11,
      category: 'safety',
      title: 'Blocking and Reporting Users',
      description: 'How to block someone or report inappropriate behaviour.',
      icon: '🛡️',
      content: `
        <h3>Safety Features</h3>
        <h4>Blocking a User:</h4>
        <ul>
          <li><strong>From a profile:</strong> Open their profile → menu/actions → Block User → confirm</li>
          <li><strong>From a chat:</strong> Open the conversation → ⋮ (options) → Block User → confirm</li>
        </ul>
        <p>They are not notified. Blocked users cannot see your profile, message you, or find you in discovery.</p>
        <h4>Reporting a User:</h4>
        <ul>
          <li><strong>From a profile:</strong> Open their profile → Report User</li>
          <li><strong>From a 1:1 chat:</strong> Open the conversation → ⋮ (options) → Report User</li>
        </ul>
        <ol>
          <li>Choose the category that best fits (safety concerns, profile issues, spam, etc.)</li>
          <li>Add details when prompted — serious safety reports ask for a short description of what happened</li>
          <li>Optionally block the person in the same flow</li>
          <li>Submit — our team reviews reports; you do not upload screenshots in the report form</li>
        </ol>
        <p>If you feel unsafe or are in immediate danger, contact local emergency services or someone you trust first.</p>
      `,
    },
    {
      id: 13,
      category: 'safety',
      title: 'Verification Badges Explained',
      description: 'What public and discreet verification badges mean on profiles.',
      icon: '✓',
      content: `
        <h3>Verification badges</h3>
        <p>Some profiles show a verification badge when the person completed Strapin&apos;s identity checks and chose to display it.</p>
        <h4>Public verification (blue seal)</h4>
        <p>They matched their profile photos with a live selfie and liveness check. Their photos show a verified match.</p>
        <h4>Discreet verification (purple shield)</h4>
        <p>They completed verification privately. Their profile photos were not used in the check.</p>
        <p>Tap any badge on a profile, discover card, or poke to read the full explanation. Verified users can turn badges off in settings — if there is no badge, they may still be verified but chose not to show it publicly.</p>
      `,
    },
    {
      id: 12,
      category: 'safety',
      title: 'Privacy and Security Settings',
      description: 'Control your privacy, data, visibility, and account security.',
      icon: '🔒',
      content: `
        <h3>Your Privacy Controls</h3>
        <ul>
          <li><strong>Appearance Mode:</strong> Switch between Light and Dark from Settings</li>
          <li><strong>Blocked Users:</strong> Settings → Blocked Users List — review or unblock at any time</li>
          <li><strong>Delete Account:</strong> Settings → Delete Account — your profile is immediately hidden and data removed within 30 days</li>
          <li><strong>Logout:</strong> Settings → Logout</li>
        </ul>
        <p><strong>Always on:</strong> Screenshot protection cannot be disabled — it protects every user automatically.</p>
      `,
    },
  ];

  const filteredArticles = helpArticles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-onyx text-mist">
      <Header />

      <PageHero
        tag="📚 Help Center"
        title="How Can We"
        titleAccent="Help?"
        description="Guides, tutorials, and answers for every part of the Strapin experience."
        compact
      >
        <div className="relative mx-auto w-full max-w-md">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">🔍</span>
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-full border border-primary/30 bg-white/[0.06] py-3.5 pl-11 pr-4 text-white outline-none transition focus:border-primary"
          />
        </div>
      </PageHero>

      <div className="site-container py-16 md:py-20">
        <div className="mb-10">
          <h2 className="mb-4 text-lg font-bold text-white">Browse by Topic</h2>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
                  selectedCategory === cat.id
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'border-white/10 text-silver hover:border-white/20'
                }`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>

        {selectedArticle ? (
          <div className="glass-card p-8 md:p-10">
            <button
              type="button"
              onClick={() => setSelectedArticle(null)}
              className="mb-6 flex items-center gap-2 font-bold text-primary"
            >
              ← Back to Articles
            </button>
            <div className="mb-4 text-4xl">{selectedArticle.icon}</div>
            <h2 className="mb-6 text-2xl font-black text-white md:text-3xl">{selectedArticle.title}</h2>
            <div className="content-prose" dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
          </div>
        ) : (
          <>
            <p className="mb-6 text-sm text-silver">
              {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'} found
              {searchQuery && ` for "${searchQuery}"`}
            </p>

            {filteredArticles.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mb-4 text-4xl">🔍</div>
                <h3 className="mb-2 font-bold text-white">No articles found</h3>
                <p className="mb-6 text-silver">Try a different search term or category</p>
                <button type="button" className="btn-primary" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {filteredArticles.map((article) => (
                  <button
                    key={article.id}
                    type="button"
                    onClick={() => setSelectedArticle(article)}
                    className="glass-card-hover p-6 text-left"
                  >
                    <div className="mb-3 text-3xl">{article.icon}</div>
                    <h3 className="mb-2 font-bold text-white">{article.title}</h3>
                    <p className="mb-4 text-sm leading-relaxed text-silver">{article.description}</p>
                    <span className="text-sm font-bold text-primary">Read more →</span>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-14 rounded-3xl border border-primary/20 bg-primary/10 p-8 text-center md:p-10">
              <h3 className="mb-2 text-xl font-black text-white">Still Need Help?</h3>
              <p className="mx-auto mb-6 max-w-lg text-silver">
                Can't find what you're looking for? Our support team responds within 24 hours.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/support" className="btn-primary">💬 Contact Support</Link>
                <a href="mailto:support@strapinapp.com" className="btn-ghost">📧 support@strapinapp.com</a>
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default Help;
